import React, { useEffect, useState } from "react";
import { Upload, Trash2, X, Crosshair } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-amber-500 bg-white";

export default function AdminMedia() {
  const [media, setMedia] = useState(null);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/media").then(({ data }) => setMedia(data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "));
      await api.post("/admin/media", fd);
      toast.success("Photo uploaded");
      load();
    } catch (err) {
      toast.error(fmtErr(err, "Upload failed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveMeta = async () => {
    try {
      await api.patch(`/admin/media/${selected.id}`, { alt: selected.alt, focal: selected.focal });
      toast.success("Photo details saved");
      setMedia(media.map((m) => (m.id === selected.id ? selected : m)));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this photo permanently? Sections already using it will keep their copy, but it will leave the library.")) return;
    await api.delete(`/admin/media/${m.id}`);
    toast.success("Photo deleted");
    setSelected(null);
    load();
  };

  const focalBtn = (x, y) => (
    <button
      key={`${x}-${y}`}
      onClick={() => setSelected({ ...selected, focal: { x, y } })}
      aria-label={`Focus ${x}% ${y}%`}
      data-testid={`focal-${x}-${y}`}
      className={`w-7 h-7 rounded-md border text-[9px] font-bold ${selected.focal?.x === x && selected.focal?.y === y ? "bg-amber-500 border-amber-600 text-[#0B1D3A]" : "border-slate-300 text-slate-400 hover:border-amber-500"}`}
    >
      ·
    </button>
  );

  return (
    <div data-testid="admin-media">
      <div className="mb-6">
        <h2 className="font-heading text-3xl font-semibold text-[#0B1D3A]">Media Library</h2>
        <p className="text-slate-500 text-sm">Your reusable photo library. Upload from your phone or computer — photos are automatically resized for fast loading.</p>
      </div>

      <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-amber-400/60 rounded-xl py-8 cursor-pointer text-[#0B1D3A] font-semibold bg-white hover:bg-amber-50 transition-colors mb-6 ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="media-upload-button">
        <Upload className="w-5 h-5 text-amber-600" />
        {uploading ? "Uploading…" : "Upload a photo (JPG, PNG, WebP — max 5 MB)"}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
      </label>

      {media === null ? (
        <div className="py-16 flex justify-center"><div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-600 rounded-full animate-spin" /></div>
      ) : media.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">No photos uploaded yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map((m) => (
            <button key={m.id} onClick={() => setSelected(m)} data-testid={`media-item-${m.id}`}
              className="group relative rounded-xl overflow-hidden border border-slate-200 hover:border-amber-500 transition-colors aspect-video bg-white">
              <img src={m.url} alt={m.alt || ""} className="w-full h-full object-cover" style={{ objectPosition: `${m.focal?.x ?? 50}% ${m.focal?.y ?? 50}%` }} />
              <span className="absolute inset-x-0 bottom-0 bg-[#061325]/75 text-amber-100 text-[11px] px-2 py-1 truncate">{m.alt || m.filename}</span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[100] bg-[#061325]/70 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="media-detail-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading text-2xl font-semibold text-[#0B1D3A]">Photo Details</h3>
              <button onClick={() => setSelected(null)} aria-label="Close" data-testid="media-detail-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <img src={selected.url} alt={selected.alt || ""} className="w-full h-56 object-cover rounded-xl border border-slate-200"
                style={{ objectPosition: `${selected.focal?.x ?? 50}% ${selected.focal?.y ?? 50}%` }} />
              <div className="space-y-4">
                <label className="block">
                  <span className="block text-xs font-semibold text-slate-600 mb-1">Alt text (for accessibility)</span>
                  <input value={selected.alt || ""} onChange={(e) => setSelected({ ...selected, alt: e.target.value })} className={inputCls} data-testid="media-alt-input" />
                </label>
                <div>
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2"><Crosshair className="w-3.5 h-3.5" /> Focal point (what stays visible on small screens)</span>
                  <div className="grid grid-cols-3 gap-1.5 w-fit">
                    {[0, 50, 100].map((y) => [0, 50, 100].map((x) => focalBtn(x, y)))}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={saveMeta} data-testid="media-save-button" className="px-4 py-2 rounded-lg bg-[#0B1D3A] text-amber-300 text-sm font-semibold">Save</button>
                  <button onClick={() => remove(selected)} data-testid="media-delete-button" className="px-4 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-semibold inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Delete</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
